import { IMetaData } from '@Models/common.model'
import { Pagination } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { $secondaryColor, $VeryLightBlue } from '@Styles/colors'
import clsx from 'clsx'
import { ChangeEvent, useEffect, useState } from 'react'
import './BasePagination.scss'

const useStyle = makeStyles({
    root: {
        '& ul': {
            display: 'inline-flex',
        },
        '& .MuiButtonBase-root': {
            color: $secondaryColor,
            fontWeight: '500',
            border: `0.5px solid ${$VeryLightBlue}`,
            transition: '0.2s all',
        },
        '& .MuiPaginationItem-previousNext': {
            border: 'none',
            background: $VeryLightBlue,
            transition: '0.2s all',
        },
        '& .Mui-selected': {
            background: 'unset',
            border: `1px solid ${$secondaryColor}`,
            transition: '0.2s all',
        },
    },
})

export interface IBasePaginationEvent {
    limit: number
    offset: number
    page: number
    pageCount: number
}

interface IBasePagination {
    metadata: IMetaData
    currentPage?: number | undefined
    pageChanged?: (params: IBasePaginationEvent) => void
}

function BasePagination(props: IBasePagination) {
    // core section
    const classes = useStyle()
    const { currentPage, metadata, pageChanged } = props
    const ac = new AbortController()

    // states section
    const [pageCount, setPageCount] = useState(10)
    const [page, setPage] = useState(1)

    useEffect(() => {
        // get quotient and check if it's less than 1
        const quotient = Number(metadata?.total) / Number(metadata?.limit)

        // sanity check
        if (metadata?.total && quotient > 1) {
            setPageCount(Math.ceil(quotient))
        } else {
            setPageCount(1)
        }
        return () => {
            ac.abort()
        }
    }, [metadata])

    useEffect(() => {
        if (!currentPage || currentPage < 1) return
        setPage(currentPage)
    }, [currentPage])

    // functions section
    const handlePageChange = (newPage: number) => {
        const { limit } = metadata
        if (!limit || page === newPage || !newPage) return
        const newOffSet = +limit * (newPage - 1)
        setPage(newPage)
        if (pageChanged)
            pageChanged({ limit, offset: newOffSet, page: newPage, pageCount })
    }

    // main
    return (
        <div className={clsx('base-pagination mt-3 text-center', classes.root)}>
            <Pagination
                count={pageCount}
                page={page}
                variant="outlined"
                onChange={(e: ChangeEvent<unknown>, newPage: number) => {
                    handlePageChange(newPage)
                }}
            />
        </div>
    )
}

export default BasePagination
